import React, { createContext, useMemo, useState } from "react";

import en from "../locales/en.json";
import zh from "../locales/zh.json";

type Translations = {
  [key: string]: {
    [key: string]: string;
  };
};

const translations: Translations = {
  en,
  zh,
};

const LOCALE_STORAGE_KEY = "locale";

const getInitialLocale = (): string => {
  try {
    const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
    return storedLocale || "zh";
  } catch (error) {
    console.error("Failed to load locale from localStorage:", error);
    return "zh";
  }
};

export const LocalizationContext = createContext({
  locale: "zh",
  setLocale: (locale: string) => {},
  t: (key: string, variables?: { [key: string]: string | number }) => key,
});

export const LocalizationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [localeState, setLocaleState] = useState(getInitialLocale());

  const setLocale = (newLocale: string) => {
    setLocaleState(newLocale);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    } catch (error) {
      console.error("Failed to save locale to localStorage:", error);
    }
  };

  const t = (key: string, variables?: { [key: string]: string | number }) => {
    let translation = translations[localeState][key] || key;
    if (variables) {
      Object.keys(variables).forEach((variable) => {
        const regex = new RegExp(`{${variable}}`, "g");
        translation = translation.replace(regex, String(variables[variable]));
      });
    }
    return translation;
  };

  const contextValues = useMemo(
    () => ({ locale: localeState, setLocale, t }),
    [localeState, setLocale, t]
  );

  return (
    <LocalizationContext.Provider value={contextValues}>
      {children}
    </LocalizationContext.Provider>
  );
};
